import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface ILanguageOption
{
    value: string;
    label: string;
}

export const CURRENT_LANGUAGE_STORAGE_KEY = 'r2.language';

@Injectable({
    providedIn: 'root'
})
export class LanguageOptionsService
{
    private readonly AVAILABLE_LANGUAGES_STORAGE_KEY = 'r2.available-languages';
    private readonly baseLanguages: ILanguageOption[] = [
        { value: 'en', label: 'English' },
        { value: 'es', label: 'Español' },
        { value: 'pt', label: 'Português' },
        { value: 'uk', label: 'Українська' },
    ];
    private readonly optionalLanguages: Record<string, ILanguageOption> = {
        ru: { value: 'ru', label: 'Русский' },
    };
    private readonly languages = new BehaviorSubject<ILanguageOption[]>(this.readInitialLanguages());

    public getLanguages(): Observable<ILanguageOption[]>
    {
        return this.languages.asObservable();
    }

    public getLanguagesValue(): ILanguageOption[]
    {
        return this.languages.getValue();
    }

    public hasLanguage(language: string): boolean
    {
        const normalizedLanguage = this.normalizeLanguage(language);
        if (!normalizedLanguage)
        {
            return false;
        }

        return this.getLanguagesValue().some(({ value }) => value === normalizedLanguage);
    }

    public isToggleableLanguage(language: string): boolean
    {
        const normalizedLanguage = this.normalizeLanguage(language);
        return Boolean(normalizedLanguage && this.optionalLanguages[ normalizedLanguage ]);
    }

    public toggleLanguage(language: string): boolean | null
    {
        const normalizedLanguage = this.normalizeLanguage(language);
        if (!normalizedLanguage || !this.optionalLanguages[ normalizedLanguage ])
        {
            return null;
        }

        const hasLanguage = this.hasLanguage(normalizedLanguage);
        const nextLanguages = hasLanguage
            ? this.getLanguagesValue().filter(({ value }) => value !== normalizedLanguage)
            : [ ...this.getLanguagesValue(), this.optionalLanguages[ normalizedLanguage ] ];
        const sortedLanguages = this.sortLanguages(nextLanguages);

        this.languages.next(sortedLanguages);
        this.persistLanguages(sortedLanguages);

        return !hasLanguage;
    }

    public getFallbackLanguage(excludedLanguage?: string): string
    {
        const normalizedLanguage = this.normalizeLanguage(excludedLanguage);
        return this.getLanguagesValue().find(({ value }) => value !== normalizedLanguage)?.value ?? 'en';
    }

    public normalizeLanguage(language?: string | null): string | undefined
    {
        const normalizedLanguage = language?.split('-')[ 0 ].toLowerCase().slice(0, 2);
        return normalizedLanguage || undefined;
    }

    private readInitialLanguages(): ILanguageOption[]
    {
        const storedLanguages = this.readStoredLanguages();

        if (storedLanguages.length)
        {
            return storedLanguages;
        }

        const initialLanguages = [ ...this.baseLanguages ];
        const currentLanguage = this.normalizeLanguage(localStorage.getItem(CURRENT_LANGUAGE_STORAGE_KEY));
        const hasRussianCandidate = (globalThis.navigator?.languages ?? [])
            .some((language) => this.normalizeLanguage(language) === 'ru');

        if (currentLanguage === 'ru' || hasRussianCandidate)
        {
            initialLanguages.push(this.optionalLanguages.ru);
        }

        return this.sortLanguages(initialLanguages);
    }

    private readStoredLanguages(): ILanguageOption[]
    {
        const storedLanguages = localStorage.getItem(this.AVAILABLE_LANGUAGES_STORAGE_KEY);

        if (!storedLanguages)
        {
            return [];
        }

        try
        {
            const parsedLanguages = JSON.parse(storedLanguages) as unknown;
            if (!Array.isArray(parsedLanguages))
            {
                return this.sortLanguages(this.baseLanguages);
            }

            const languages = [ ...this.baseLanguages ];

            for (const language of parsedLanguages)
            {
                if (typeof language !== 'string')
                {
                    continue;
                }

                const normalizedLanguage = this.normalizeLanguage(language);
                if (normalizedLanguage && this.optionalLanguages[ normalizedLanguage ])
                {
                    languages.push(this.optionalLanguages[ normalizedLanguage ]);
                }
            }

            return this.sortLanguages(languages);
        } catch
        {
            return this.sortLanguages(this.baseLanguages);
        }
    }

    private persistLanguages(languages: ILanguageOption[]): void
    {
        const optionalLanguageCodes = languages
            .map(({ value }) => value)
            .filter((value) => Boolean(this.optionalLanguages[ value ]));

        localStorage.setItem(this.AVAILABLE_LANGUAGES_STORAGE_KEY, JSON.stringify(optionalLanguageCodes));
    }

    private sortLanguages(languages: ILanguageOption[]): ILanguageOption[]
    {
        const uniqueLanguages = new Map<string, ILanguageOption>();

        for (const language of [ ...this.baseLanguages, ...languages ])
        {
            if (!uniqueLanguages.has(language.value))
            {
                uniqueLanguages.set(language.value, language);
            }
        }

        return Array.from(uniqueLanguages.values())
            .sort((a, b) => a.value.localeCompare(b.value));
    }
}
